import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Radar, ClipboardList, Scale, ShieldAlert, TriangleAlert, RotateCcw, CalendarClock, ArrowRight, Hourglass } from "lucide-react";

const TYPE_META = {
  action: { label: "Action", icon: ClipboardList, color: "#2563EB", go: "actions" },
  decision: { label: "Decision", icon: Scale, color: "#7C3AED", go: "decisions" },
  risk: { label: "Risk", icon: ShieldAlert, color: "#DC2626", go: "risks" },
  issue: { label: "Issue", icon: TriangleAlert, color: "#EA580C", go: "risks" },
};

export default function OnTheRadar({ projectId, onGo }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/projects/${projectId}/radar`)
    .then(({ data }) => setData(data))
    .catch(() => setData({ items: [], count: 0, due_count: 0 }));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const reactivate = async (it) => {
    setBusy(it.id);
    try {
      const body = { ...it.item, status: "open" };
      if (it.type === "action") await api.put(`/action-items/${it.id}`, body);
      else if (it.type === "decision") await api.put(`/decisions/${it.id}`, body);
      else await api.put(`/risks/${it.id}`, body);
      toast.success(`"${it.title}" is active again`);
      load();
    } catch (e) { toast.error("Could not reactivate"); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const items = data.items || [];

  return (
    <div className="space-y-5" data-testid="radar-view">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2.5">
          <span className="h-10 w-10 rounded-full bg-sky-100 flex items-center justify-center"><Radar className="h-5 w-5 text-sky-600" /></span>
          <div>
            <h3 className="font-display font-bold text-lg leading-tight">On the Radar</h3>
            <p className="text-xs text-muted-foreground">Parked & on-hold items that must not be forgotten.</p>
          </div>
        </div>
        <div className="flex gap-2 ml-auto">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold" data-testid="radar-count-chip"><Radar className="h-3.5 w-3.5 text-sky-600" /> {data.count} parked</span>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${data.due_count ? "bg-red-100 text-red-700" : "border border-border text-muted-foreground"}`} data-testid="radar-due-chip"><CalendarClock className="h-3.5 w-3.5" /> {data.due_count} due to revisit</span>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="radar-empty">
          <Radar className="h-8 w-8 text-sky-500 mx-auto mb-3" />
          <p className="font-semibold">Nothing parked right now.</p>
          <p className="text-sm text-muted-foreground mt-1">Mark any action item, decision, risk or issue as <b>On Hold / Parked</b> and it will land here with its revisit date.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const m = TYPE_META[it.type] || TYPE_META.action;
            const Icon = m.icon;
            return (
              <div key={`${it.type}-${it.id}`}
                className={`border rounded-lg p-4 ${it.due ? "border-red-300 bg-red-50" : it.due_soon ? "border-amber-300 bg-amber-50" : "border-border"}`}
                data-testid={`radar-item-${it.id}`}>
                <div className="flex items-start gap-3">
                  <span className="h-7 w-7 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${m.color}1a` }}>
                    <Icon className="h-4 w-4" style={{ color: m.color }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ color: m.color, background: `${m.color}1a` }}>{m.label}</span>
                      <p className="font-medium text-sm">{it.title}</p>
                      {it.owner && <span className="text-[11px] text-muted-foreground">· {it.owner}</span>}
                      {it.project_name && <span className="text-[11px] text-muted-foreground">· {it.project_name}</span>}
                    </div>
                    {it.reason && <p className="text-xs text-muted-foreground italic mt-1" data-testid={`radar-reason-${it.id}`}>“{it.reason}”</p>}
                    <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                      <span className={`inline-flex items-center gap-1 font-bold px-1.5 py-0.5 rounded ${it.due ? "bg-red-500 text-white" : it.due_soon ? "bg-amber-400 text-amber-950" : "bg-secondary text-muted-foreground"}`} data-testid={`radar-revisit-${it.id}`}>
                        <CalendarClock className="h-3 w-3" /> {it.revisit ? `Revisit ${it.revisit}` : "No revisit date"}{it.due ? " — due now" : it.due_soon ? " — coming up" : ""}
                      </span>
                      {it.age_days != null && (
                        <span className={`inline-flex items-center gap-1 font-semibold px-1.5 py-0.5 rounded ${it.stale ? "bg-orange-100 text-orange-700" : "text-muted-foreground"}`} title={it.stale ? "This has been parked for a while — consider revisiting or closing it" : "Time since it was parked"} data-testid={`radar-age-${it.id}`}>
                          <Hourglass className="h-3 w-3" /> parked {it.age_days}d{it.stale ? " — parked too long" : ""}
                        </span>
                      )}
                      {it.parked_at && <span className="text-muted-foreground">parked {String(it.parked_at).slice(0, 10)}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={busy === it.id} onClick={() => reactivate(it)} data-testid={`radar-reactivate-${it.id}`}>
                      {busy === it.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Reactivate
                    </Button>
                    {onGo && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground" onClick={() => onGo(m.go)} data-testid={`radar-open-${it.id}`}>
                        Open <ArrowRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
